import { getPropertyData } from '@/apis/apis'
import useDocumentTitle from '@/components/Helmet'
import { useCustomLoader } from '@/components/Loader'
import GlobalAutocomplete from '@/components/material/Autocomplete'
import { Button } from '@/components/ui/button'
import { useToast } from '@/hooks/use-toast'
import axiosInstance from '@/lib/axiosInstance'
import { zodResolver } from '@hookform/resolvers/zod'
import { Box, FormControl, Grid, InputLabel, MenuItem, Select, TextField, Typography } from '@mui/material'
import { ArrowLeft } from 'lucide-react'
import React, { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useLocation, useParams } from 'wouter'
import { z } from 'zod'

const residentSchema = z.object({
    firstName: z.string().min(1, "First name is required"),
    lastName: z.string().min(1, "Last name is required"),
    email: z.string().email("Please enter a valid email address"),
    phone: z.string().min(10, "Phone number is required"),
    propertyId: z.object({
        _id: z.string().min(1, "Property is required"),
        name: z.string().optional(),
        units: z.array(z.string()).optional()
    }),
    unitId: z.string().min(1, "Unit is required"),
    active: z.boolean(),
})

const ResidentAdd = () => {
    const { id } = useParams();
    useDocumentTitle(` ${id ? 'Edit' : 'Add'} Resident`);
    const { toast } = useToast();
    const loader = useCustomLoader();
    const [_, navigate] = useLocation();
    const [propertySearch, setPropertySearch] = useState("");
    const [properties, setProperties] = useState<any[]>([]);


    const { control, formState: { errors }, handleSubmit, reset, watch } = useForm({
        resolver: zodResolver(residentSchema),
        defaultValues: {
            firstName: '',
            lastName: '',
            email: '',
            phone: '',
            propertyId: {
                _id: '', 
                name: '', 
                units: []
            },
            unitId: [''],
            active: true
        }
    })

    useEffect(() => {
        if (id) {
            (async () => {
                try {
                    const { data: resident } = await axiosInstance.get(`/api/residents/${id}`);
                    reset(resident);
                } catch (error) {
                    toast({
                        variant: "destructive",
                        title: "Error!",
                        description: "Something went wrong",
                    })
                }
            })();
        }
    }, [id])

    useEffect(() => {
        const fetchData = async () => {
            const filters = {
                search: propertySearch,
            }
            const data = await getPropertyData({ filters, page: 0, rowsPerPage: 10 });
            setProperties(data.data);
        }
        const timer = setTimeout(() => {
            fetchData();
        }, propertySearch ? 600 : 0);
        return () => clearTimeout(timer);
    }, [])
    const onSubmit = async (payload: any) => {
        loader(true);
        try {
            payload.propertyId = payload.propertyId._id;
            let response = {
                success: false,
                message: ''
            };
            if (id) {
                const { data } = await axiosInstance.put(`/api/residents/${id}`, payload);
                response = data;
            }
            else {
                const { data } = await axiosInstance.post('/api/residents', payload);
                response = data;
            }
            if (response.success) {
                toast({
                    variant: "success",
                    title: "Success!",
                    description: response.message,
                });
                navigate('/resident');
            } else {
                toast({
                    variant: "destructive",
                    title: "Error!",
                    description: response.message || "Something went wrong",
                });
            }

        } catch (error: any) {
            toast({
                variant: "destructive",
                title: "Error!",
                description: error?.response?.data?.message || "Something went wrong",
            });
        }
        finally {
            loader(false);
        }

    }
    return (
        <div>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Button variant='default' onClick={() => navigate("/resident")}><ArrowLeft /> Back</Button>
                <Typography sx={{ textAlign: "center", fontWeight: 600, fontSize: 24 }}>{id ? 'Update' : 'Add'} Resident</Typography>
                <Typography></Typography>
            </Box>

            <form onSubmit={handleSubmit(onSubmit)}>
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Controller
                            name="firstName"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    label="First Name"
                                    fullWidth
                                    error={!!errors.firstName}
                                    helperText={errors.firstName ? errors.firstName.message : ''}
                                    placeholder='Enter first name'
                                />
                            )}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Controller
                            name="lastName"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    label="Last Name"
                                    fullWidth
                                    error={!!errors.lastName}
                                    helperText={errors.lastName ? errors.lastName.message : ''}
                                    placeholder='Enter last name'
                                />
                            )}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Controller
                            name="email"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    label="Email"
                                    fullWidth
                                    error={!!errors.email}
                                    helperText={errors.email ? errors.email.message : ''}
                                    placeholder='Enter email'
                                />
                            )}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Controller
                            name="phone"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    label="Phone"
                                    fullWidth
                                    error={!!errors.phone}
                                    helperText={errors.phone ? errors.phone.message : ''}
                                    placeholder='Enter phone number'
                                />
                            )}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Controller
                            name="propertyId"
                            control={control}
                            render={({ field }) => (
                                <GlobalAutocomplete
                                    label="Property"
                                    options={properties}
                                    value={field.value}
                                    onChange={(newValue) => field.onChange(newValue)}
                                    placeholder="Select or search property"
                                    getOptionLabel={(option: any) => option.name || ''}
                                    onInputChange={(value) => setPropertySearch(value)}
                                    error={!!errors.propertyId}
                                    helperText={errors?.propertyId?._id ? errors.propertyId._id.message : ''}
                                />
                            )}
                        />
                        {/* {
                            errors.propertyId?._id && <Typography color="error" variant="body2">{errors.propertyId?._id.message}</Typography>
                        } */}
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Controller
                            name="unitId"
                            control={control}
                            render={({ field }) => (
                                <GlobalAutocomplete
                                    label="Unit"
                                    options={watch("propertyId")?.units || []}
                                    value={field.value || ""}
                                    onChange={(newValue) => field.onChange(newValue ?? "")}
                                    placeholder="Select or enter unit"
                                    getOptionLabel={(option: any) => option || ''}
                                    error={!!errors.unitId}
                                    helperText={!watch("propertyId")?._id ? "Please select property first" : errors?.unitId?.message || ''}
                                />
                            )}
                        />
                        {/* {
                            errors.unitId && <Typography color="error" variant="body2">{errors.unitId.message}</Typography>
                        } */}
                    </Grid>
                </Grid>
                <Grid container spacing={2} mt={2}>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Button type="button" onClick={() => navigate("/resident")} variant="error">
                            Cancel
                        </Button>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }} sx={{ display: "flex", justifyContent: { xs: "flex-start", md: "flex-end" } }}>
                        <Button type="submit" variant="default">
                            {id ? "Update" : "Submit"}
                        </Button>
                    </Grid>
                </Grid>
            </form>
        </div>
    )
}

export default ResidentAdd